import { useState, type FormEvent, type JSX } from "react";

import type { CalendarDTO, MemberDTO } from "../../../common/api/types.js";
import { PrimaryButton } from "../../../common/components/PrimaryButton.js";
import { useMembers } from "../../../common/people/peopleQueries.js";
import { toLocalInput } from "../weekRange.js";

export interface EventDraft {
  calendarId: string;
  title: string;
  startsAt: string;
  endsAt: string;
  location?: string;
  attendeeIds?: string[];
  withMeeting?: boolean;
}

/** Neuer Termin. Teilnehmer und Meeting sind optional. */
export function EventForm({
  calendars,
  initialStart,
  initialEnd,
  onSubmit,
  onCancel,
  busy,
  error,
}: {
  calendars: CalendarDTO[];
  initialStart: Date;
  initialEnd?: Date;
  onSubmit: (draft: EventDraft) => void;
  onCancel: () => void;
  busy: boolean;
  error: string | null;
}): JSX.Element {
  const writable = calendars.filter((calendar) => calendar.level !== "VIEW");
  const defaultStart = new Date(initialStart);
  if (defaultStart.getHours() === 0 && defaultStart.getMinutes() === 0) {
    defaultStart.setHours(9, 0, 0, 0);
  }
  const defaultEnd = initialEnd ? new Date(initialEnd) : new Date(defaultStart.getTime() + 60 * 60_000);

  const [calendarId, setCalendarId] = useState(writable[0]?.id ?? "");
  const [title, setTitle] = useState("");
  const [startsAt, setStartsAt] = useState(toLocalInput(defaultStart));
  const [endsAt, setEndsAt] = useState(toLocalInput(defaultEnd));
  const [location, setLocation] = useState("");
  const [withMeeting, setWithMeeting] = useState(false);
  const [attendeeIds, setAttendeeIds] = useState<string[]>([]);
  const [localError, setLocalError] = useState<string | null>(null);

  const members = useMembers("");

  const toggleAttendee = (userId: string): void => {
    setAttendeeIds((current) =>
      current.includes(userId) ? current.filter((id) => id !== userId) : [...current, userId],
    );
  };

  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (title.trim().length === 0) {
      setLocalError("Bitte einen Titel angeben.");
      return;
    }
    if (new Date(endsAt) <= new Date(startsAt)) {
      setLocalError("Das Ende muss nach dem Anfang liegen.");
      return;
    }
    setLocalError(null);
    onSubmit({
      calendarId,
      title: title.trim(),
      // Die Eingabe ist lokale Zeit; das Backend erwartet UTC.
      startsAt: new Date(startsAt).toISOString(),
      endsAt: new Date(endsAt).toISOString(),
      ...(location.trim() ? { location: location.trim() } : {}),
      ...(attendeeIds.length > 0 ? { attendeeIds } : {}),
      ...(withMeeting ? { withMeeting: true } : {}),
    });
  };

  return (
    <div className="fixed inset-0 z-30 flex items-end justify-center bg-black/40 sm:items-center sm:p-6">
      <form
        onSubmit={submit}
        noValidate
        className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-t-2xl border border-line bg-surface-raised p-5 pb-[calc(theme(spacing.5)+env(safe-area-inset-bottom))] shadow-2xl sm:rounded-2xl sm:pb-5"
      >
        <h2 className="mb-4 text-base font-semibold text-ink">Neuer Termin</h2>

        {writable.length === 0 ? (
          <p className="text-sm text-danger">
            Kein Kalender mit Schreibrecht vorhanden.
          </p>
        ) : (
          <div className="space-y-3">
            <Field label="Titel">
              <input
                value={title}
                onChange={(changed) => setTitle(changed.target.value)}
                required
                className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-ink"
              />
            </Field>

            <Field label="Kalender">
              <select
                value={calendarId}
                onChange={(changed) => setCalendarId(changed.target.value)}
                className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-ink"
              >
                {writable.map((calendar) => (
                  <option key={calendar.id} value={calendar.id}>
                    {calendar.name}
                    {calendar.ownedByMe ? "" : ` (${calendar.ownerDisplayName})`}
                  </option>
                ))}
              </select>
            </Field>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="Beginn">
                <input
                  type="datetime-local"
                  value={startsAt}
                  onChange={(changed) => setStartsAt(changed.target.value)}
                  className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-ink"
                />
              </Field>
              <Field label="Ende">
                <input
                  type="datetime-local"
                  value={endsAt}
                  onChange={(changed) => setEndsAt(changed.target.value)}
                  className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-ink"
                />
              </Field>
            </div>

            <Field label="Ort (optional)">
              <input
                value={location}
                onChange={(changed) => setLocation(changed.target.value)}
                className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-ink"
              />
            </Field>

            <label className="flex items-center gap-2 text-sm text-ink">
              <input
                type="checkbox"
                checked={withMeeting}
                onChange={(changed) => setWithMeeting(changed.target.checked)}
              />
              Meeting-Raum anlegen
            </label>

            <fieldset>
              <legend className="mb-1 text-sm font-medium text-ink">Teilnehmer</legend>
              <ul className="max-h-40 space-y-1 overflow-y-auto rounded-lg border border-line p-1">
                {(members.data ?? []).map((member: MemberDTO) => (
                  <li key={member.userId}>
                    <label className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-surface-sunken">
                      <input
                        type="checkbox"
                        checked={attendeeIds.includes(member.userId)}
                        onChange={() => toggleAttendee(member.userId)}
                      />
                      <span className="min-w-0 truncate text-ink">
                        {member.displayName}
                        {member.kind === "AGENT" ? " · Agent" : ""}
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            </fieldset>
          </div>
        )}

        {localError ? <p className="mt-3 text-sm text-danger">{localError}</p> : null}
        {error ? <p className="mt-3 text-sm text-danger">{error}</p> : null}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg px-3 py-2 text-sm text-ink-muted hover:bg-surface-sunken hover:text-ink"
          >
            Abbrechen
          </button>
          <PrimaryButton type="submit" disabled={busy || writable.length === 0}>
            {busy ? "Wird angelegt …" : "Anlegen"}
          </PrimaryButton>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }: { label: string; children: JSX.Element }): JSX.Element {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-ink">{label}</span>
      {children}
    </label>
  );
}
